import type { StorefrontProduct } from '../../api/client';
import type { StoreCollection } from './useStoreCatalog';

// What an installed theme draws where the store has nothing yet: neutral stand-ins, the way Shopify fills
// a newly added theme with "Example product title" cards and line drawings. The page keeps the theme's whole
// layout from the first day, nothing pretends to be a real product, and every stand-in is replaced the moment
// the store has a product or a collection of its own for that place. They carry the `sample-` id every buying
// path already refuses, so a stand-in can be looked at but never added to a cart.

/** The title every stand-in product carries, as Shopify's do. */
export const PLACEHOLDER_TITLE = 'Example product title';

/** The price a stand-in shows, in whatever currency the store sells in. */
const PLACEHOLDER_PRICE = '19.99';

/** The sentence a stand-in product's description holds, telling the merchant what belongs there. */
const PLACEHOLDER_DESCRIPTION = '<p>Use this space to describe your product: what it is, how it feels and why your customers will love it.</p>';

/** The line drawings, served by the storefront itself: an SVG is never inlined as data, since one could carry script.
 * Versioned, so a redrawn picture is never answered from an edge cache holding the old one — or the page a missing
 * file was answered with before it was deployed. */
export const PRODUCT_ART = '/placeholders/product.svg?v=1';
export const COLLECTION_ART = '/placeholders/collection.svg?v=1';

// Builds the stand-in product for one position, so its card opens the example product at that position.
export function placeholderProduct(slot: number): StorefrontProduct {
  const id = `sample-${slot}`;
  return {
    id, slug: id, title: PLACEHOLDER_TITLE, status: 'DRAFT', productType: 'Type', descriptionHtml: PLACEHOLDER_DESCRIPTION,
    variants: [{ id, title: 'Default', price: PLACEHOLDER_PRICE, inventoryQuantity: 0 }],
    images: [{ id, src: PRODUCT_ART, position: 0 }]
  } as StorefrontProduct;
}

// Builds a row of stand-in products, one per card the row would draw.
export const placeholderProducts = (count: number): StorefrontProduct[] =>
  Array.from({ length: Math.max(0, Math.trunc(count)) }, (_unused, slot) => placeholderProduct(slot));

// Builds a row of stand-in collections, named as the theme names them where it does.
export function placeholderCollections(count: number, titles: readonly string[]): StoreCollection[] {
  return Array.from({ length: Math.max(0, Math.trunc(count)) }, (_unused, index) => ({
    id: `sample-collection-${index}`, slug: 'all', title: titles[index] ?? 'Collection title', imageUrl: COLLECTION_ART, productCount: 0
  }));
}
